import React from 'react';
import {Box, Button, DialogActions, DialogContent, DialogTitle, Typography} from '@mui/material';
import {ArchiveOutlined, RestoreOutlined} from '@mui/icons-material';
import {alpha} from '@mui/material/styles';
import {SettingsDialog} from '../settings/SettingsDialog';
import {getSettingsColors} from '../settings/SettingsDesign';

export const flowSelectPaperStyles = {
    bgcolor: 'background.dropdown',
    color: 'text.primary', borderRadius: '10px', border: '1px solid', borderColor: 'divider',
    '& .MuiAutocomplete-option': {fontSize: 13, minHeight: '40px !important',
        '&.Mui-focused, &[aria-selected="true"]': {bgcolor: theme => alpha(theme.palette.primary.main, 0.1)}}
};

export function FlowDialog({sx = [], ...props}) {
    return <SettingsDialog {...props} sx={[{
        '& .MuiDialogContent-root': {position: 'relative', minWidth: 0},
        '& .MuiDialogTitle-root + .MuiDialogContent-root': {pt: 1.5},
        '& .MuiDialog-paper': {
            '& .MuiTextField-root': {my: 0},
            '& .MuiOutlinedInput-root': {bgcolor: theme => theme.palette.mode === 'light' ? theme.palette.background.paper : getSettingsColors(theme).input},
            '& .flow-routing-row .MuiOutlinedInput-root': {
                bgcolor: theme => getSettingsColors(theme).input
            },
            '& .flow-routing-row .MuiOutlinedInput-input': {px: 1, fontSize: 12},
            '& .flow-routing-row .MuiInputLabel-root': {fontSize: 12},
            '& .flow-routing-row .MuiInputAdornment-root': {ml: 0.5},
            '& .MuiAutocomplete-root .MuiOutlinedInput-root, & .MuiOutlinedInput-root.MuiSelect-root': {bgcolor: theme => getSettingsColors(theme).input},
            '& .campaign-surface-inputs .MuiOutlinedInput-root, & .flow-details-inputs .MuiOutlinedInput-root, & .flow-conditions [role="tabpanel"] .MuiOutlinedInput-root': {
                bgcolor: theme => theme.palette.mode === 'light' ? theme.palette.background.paper : getSettingsColors(theme).input
            },
            '& .flow-conditions > .MuiTextField-root .MuiOutlinedInput-root': {bgcolor: theme => getSettingsColors(theme).input},
            '& .flow-conditions [role="tabpanel"] .MuiButton-root': {minHeight: 30, py: 0.25, px: 1, fontSize: 12},
            '& .MuiOutlinedInput-input': {fontSize: 13},
            '& .MuiButton-root': {textTransform: 'none', boxShadow: 'none', minHeight: 36},
            '& .MuiAlert-root': {borderRadius: '10px', fontSize: 13, lineHeight: 1.7}
        }
    }, ...(Array.isArray(sx) ? sx : [sx])]}/>;
}

export function FlowSection({title, description, action, children, sx = []}) {
    const titleId = React.useId();
    return <Box component="section" aria-labelledby={titleId} sx={[{p: {xs: 2, sm: 2.5}, borderRadius: '12px',
        minWidth: 0, bgcolor: theme => getSettingsColors(theme).inset}, ...(Array.isArray(sx) ? sx : [sx])]}>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minWidth: 0}}>
            <Typography component="h3" id={titleId} sx={{fontSize: 14, fontWeight: 600, minWidth: 0}}>
                {title}
            </Typography>
            {action && <Box sx={{display: 'flex', flexShrink: 0}}>{action}</Box>}
        </Box>
        {description && <Typography sx={{fontSize: 13, color: 'text.secondary', mt: 0.5, lineHeight: 1.6}}>{description}</Typography>}
        <Box sx={{mt: 2, display: 'grid', gap: 2, minWidth: 0}}>{children}</Box>
    </Box>;
}

export function FlowConfirmationDialog({confirm, action = 'archive', selectedCount = 1}) {
    const restoring = action === 'restore';
    const Icon = restoring ? RestoreOutlined : ArchiveOutlined;
    const label = restoring ? 'Restore' : 'Archive';
    const flows = selectedCount === 1 ? 'flow' : `${selectedCount} flows`;
    return <FlowDialog open fullWidth maxWidth="xs" onClose={() => confirm(false)}
        aria-labelledby="flow-confirm-title" aria-describedby="flow-confirm-description"
        sx={{'& .MuiDialog-paper': {borderRadius: '16px'}}}>
        <DialogTitle id="flow-confirm-title">
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                <Box sx={{display: 'flex', p: 1, borderRadius: '10px', color: restoring ? 'primary.main' : 'error.main',
                    bgcolor: theme => alpha(restoring ? theme.palette.primary.main : theme.palette.error.main, 0.1)}}><Icon/></Box>
                <Box component="span">{label} {flows}?</Box>
            </Box>
        </DialogTitle>
        <DialogContent><Typography id="flow-confirm-description" sx={{fontSize: 14, color: 'text.secondary', lineHeight: 1.7}}>
            {restoring ? 'The selected flows will return to your active flows.' :
                'The selected flows will move to your archive. You can restore them from “View archived” at any time.'}
        </Typography></DialogContent>
        <DialogActions>
            <Button color="inherit" onClick={() => confirm(false)}>Cancel</Button>
            <Button variant="contained" color={restoring ? 'primary' : 'error'} onClick={() => confirm(true)}>{label} {selectedCount === 1 ? 'flow' : 'flows'}</Button>
        </DialogActions>
    </FlowDialog>;
}
